// Orders inbox — /orders. Bucket-modes: te-verzenden / verzonden / geleverd /
// returns / cancelled / alle. Layout geïnspireerd op Sendcloud + Stockitup
// referenties (zie /voorbeelden/orders): status-tabs in parent layout,
// inline item-preview, tracking-kolom voor Verzonden, bulk-acties via
// checkbox-select + toolbar.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  bulkAddOrderNote,
  bulkExportOrdersCsv,
  bulkUpdateOrdersWorkflow,
  fetchOrders,
  fetchOrderWorkflowStates,
  OrderBucket,
  OrderListItem,
  OrdersFilter,
} from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { SortHeader, SortState, compareSort } from "../../components/SortHeader";
import { SavedViewsBar } from "../../components/SavedViewsBar";
import { useSavedViews } from "../../hooks/useSavedViews";

const fmtEUR = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const fmtNum = new Intl.NumberFormat("nl-NL");
const fmtDate = new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "short", year: "numeric" });
const fmtRelative = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const u = Math.floor(diff / 3600_000);
  if (u < 24) return `${u}u`;
  const dag = Math.floor(u / 24);
  if (dag < 7) return `${dag}d`;
  return fmtDate.format(d);
};
const fmtAbsolute = (iso: string | null) => iso ? new Date(iso).toLocaleString("nl-NL", { dateStyle: "long", timeStyle: "short" }) : "";

const WORKFLOW_LABELS: Record<string, string> = {
  received: "Ontvangen",
  payment_pending: "Wacht op betaling",
  ready_to_pick: "Klaar om te picken",
  picked: "Gepickt",
  shipped: "Verzonden",
  delivered: "Geleverd",
  cancelled: "Geannuleerd",
  returned: "Geretourneerd",
  new: "Nieuw",
  in_progress: "In behandeling",
  waiting_customer: "Wachten op klant",
  waiting_supplier: "Wachten op leverancier",
  paused: "Gepauzeerd",
  replacement_requested: "Replacement",
  refunded: "Gerestitueerd",
  closed: "Afgesloten",
};

const WORKFLOW_DOTS: Record<string, string> = {
  received: "var(--color-text-muted)",
  payment_pending: "var(--color-warning)",
  ready_to_pick: "var(--color-warning)",
  picked: "var(--color-accent)",
  shipped: "var(--color-accent)",
  delivered: "var(--color-positive)",
  closed: "var(--color-positive)",
  cancelled: "var(--color-negative)",
  returned: "var(--color-negative)",
  refunded: "var(--color-negative)",
};

type SortField = "external_id" | "sales_channel_name" | "status" | "workflow_state" | "customer_country" | "order_date" | "total_amount" | "total_margin";
type Mode = "te-verzenden" | "verzonden" | "geleverd" | "returns" | "cancelled" | "alle";

const MODE_LABELS: Record<Mode, string> = {
  "te-verzenden": "Te verzenden",
  "verzonden": "Verzonden",
  "geleverd": "Geleverd",
  "returns": "Returns",
  "cancelled": "Cancelled",
  "alle": "Alle orders",
};

interface Props {
  mode?: Mode;
}

export function OrdersInbox({ mode = "alle" }: Props) {
  const { canSeeAmounts: canSeeAmountsFor, hasPerm } = useAuth();
  const canSeeAmounts = canSeeAmountsFor("orders.view");
  const canEdit = hasPerm("orders.write") || hasPerm("orders.edit:edit");
  const navigate = useNavigate();

  // Saved-views key + default-filter per mode
  const savedKey = `orders.${mode}.views.v1`;
  const defaultFilter: OrdersFilter = mode === "alle"
    ? { limit: 200 }
    : { limit: 200, bucket: mode as OrderBucket };

  const { filter, setFilter, replaceFilter, views, saveView, applyView, deleteView } =
    useSavedViews<OrdersFilter>(savedKey, defaultFilter);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [workflowStates, setWorkflowStates] = useState<Array<{ workflow_state: string; n: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState<SortField>>({ field: "order_date", dir: "desc" });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Bucket-filter zorgt dat backend juiste subset levert. Mode is leidend voor URL.
  // Force bucket update bij navigatie naar andere mode.
  useEffect(() => {
    if (mode !== "alle" && filter.bucket !== mode) {
      setFilter({ bucket: mode as OrderBucket });
    }
    if (mode === "alle" && filter.bucket) {
      setFilter({ bucket: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const reload = () => {
    setLoading(true);
    fetchOrders(filter)
      .then(setOrders)
      .catch((e) => setError(e instanceof Error ? e.message : "Fout"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(reload, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.q, filter.status, filter.workflow_state, filter.kanaal, filter.date_from, filter.date_to, filter.bucket]);

  useEffect(() => {
    fetchOrderWorkflowStates().then(setWorkflowStates).catch(() => {});
  }, []);

  // Selection reset bij filter-change
  useEffect(() => { setSelectedIds(new Set()); }, [orders]);

  const channels = useMemo(() => {
    const set = new Set<string>();
    for (const o of orders) if (o.sales_channel_name) set.add(o.sales_channel_name);
    return Array.from(set).sort();
  }, [orders]);

  const sortedOrders = useMemo(() => {
    if (!sort.field) return orders;
    return [...orders].sort((a, b) => compareSort(a, b, sort.field! as keyof OrderListItem, sort.dir));
  }, [orders, sort]);

  const counts = useMemo(() => {
    return { total: orders.length, returns: orders.filter((o) => o.has_returns).length, notes: orders.filter((o) => o.has_notes).length };
  }, [orders]);

  const allSelected = sortedOrders.length > 0 && sortedOrders.every((o) => selectedIds.has(o.id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(sortedOrders.map((o) => o.id)));
  };
  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // === Bulk-acties ===
  const bulkMark = async (workflow_state: string, label: string) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!confirm(`${ids.length} order${ids.length === 1 ? "" : "s"} markeren als "${label}"?`)) return;
    setBulkBusy(true);
    try {
      const res = await bulkUpdateOrdersWorkflow(ids, workflow_state);
      setSelectedIds(new Set());
      reload();
      alert(`${res.updated} order(s) bijgewerkt.`);
    } catch (e) { setError(e instanceof Error ? e.message : "Fout"); }
    finally { setBulkBusy(false); }
  };

  const bulkSetWorkflow = async () => {
    const wf = prompt("Workflow-state (received/payment_pending/ready_to_pick/picked/shipped/delivered/closed/cancelled/returned/refunded):");
    if (!wf) return;
    await bulkMark(wf.trim(), wf.trim());
  };

  const bulkNote = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const note = prompt(`Bulk notitie toevoegen aan ${ids.length} order(s):`);
    if (!note?.trim()) return;
    setBulkBusy(true);
    try {
      const res = await bulkAddOrderNote(ids, note.trim());
      setSelectedIds(new Set());
      reload();
      alert(`${res.inserted} notitie(s) toegevoegd.`);
    } catch (e) { setError(e instanceof Error ? e.message : "Fout"); }
    finally { setBulkBusy(false); }
  };

  const bulkExport = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const blob = await bulkExportOrdersCsv(ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { setError(e instanceof Error ? e.message : "Fout"); }
    finally { setBulkBusy(false); }
  };

  const showTracking = mode === "verzonden";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <header className="section-head">
        <div>
          <h2>{MODE_LABELS[mode]}</h2>
          <p className="lede">
            {counts.total} orders
            {counts.returns > 0 && <> · {counts.returns} met retours</>}
            {counts.notes > 0 && <> · {counts.notes} met notities</>}
            {" · top "}{filter.limit}
          </p>
        </div>
      </header>

      {error && <div className="error">{error}<button className="btn btn-ghost" style={{ marginLeft: 8, padding: "2px 8px" }} onClick={() => setError(null)}>×</button></div>}

      <section className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
        <div className="toolbar">
          <input
            type="text"
            className="toolbar-search"
            placeholder="🔍 Zoek op order #, klant, referentie, SKU of EAN…"
            value={filter.q ?? ""}
            onChange={(e) => setFilter({ q: e.target.value })}
          />
          <select value={filter.kanaal ?? ""} onChange={(e) => setFilter({ kanaal: e.target.value || undefined })}>
            <option value="">Kanaal: alle</option>
            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {mode === "alle" && (
            <select value={filter.workflow_state ?? ""} onChange={(e) => setFilter({ workflow_state: e.target.value || undefined })}>
              <option value="">Workflow: alle</option>
              {workflowStates.map((s) => (
                <option key={s.workflow_state} value={s.workflow_state}>
                  {WORKFLOW_LABELS[s.workflow_state] || s.workflow_state} ({s.n})
                </option>
              ))}
            </select>
          )}
          <input type="date" value={filter.date_from ?? ""} onChange={(e) => setFilter({ date_from: e.target.value || undefined })} title="Vanaf" />
          <input type="date" value={filter.date_to ?? ""} onChange={(e) => setFilter({ date_to: e.target.value || undefined })} title="T/m" />
          <button
            className="btn btn-ghost"
            style={{ padding: "2px 10px", fontSize: 12 }}
            onClick={() => replaceFilter(defaultFilter)}
            title="Reset filters"
          >Reset</button>
        </div>
        <SavedViewsBar views={views} onApply={applyView} onSave={saveView} onDelete={deleteView} />
      </section>

      {/* Bulk-actie toolbar (sticky boven tabel als er selectie is) */}
      {selectedIds.size > 0 && (
        <section className="card" style={{
          padding: "var(--space-3) var(--space-4)",
          background: "var(--color-accent)22",
          border: "1px solid var(--color-accent)",
          display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
        }}>
          <strong>{selectedIds.size} order{selectedIds.size === 1 ? "" : "s"} geselecteerd</strong>
          {canEdit && (
            <>
              <button className="btn" disabled={bulkBusy} onClick={() => bulkMark("shipped", "Verzonden")}>Markeer verzonden</button>
              <button className="btn" disabled={bulkBusy} onClick={() => bulkMark("closed", "Afgesloten")}>Markeer afgesloten</button>
              <button className="btn btn-ghost" disabled={bulkBusy} onClick={bulkSetWorkflow}>Workflow…</button>
              <button className="btn btn-ghost" disabled={bulkBusy} onClick={bulkNote}>Notitie toevoegen…</button>
            </>
          )}
          <button className="btn btn-ghost" disabled={bulkBusy} onClick={bulkExport}>↓ Export CSV</button>
          <button className="btn btn-ghost" style={{ marginLeft: "auto" }} onClick={() => setSelectedIds(new Set())}>Selectie wissen</button>
        </section>
      )}

      <section className="card" style={{ padding: 0 }}>
        {loading && orders.length === 0 ? (
          <div style={{ padding: "var(--space-5)", color: "var(--color-text-muted)" }}>Laden…</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: "var(--space-5)", color: "var(--color-text-muted)" }}>Geen orders in deze selectie.</div>
        ) : (
          <div className="scroll-wrap" style={{ margin: 0, padding: 0 }}>
            <table className="data sticky-first-col">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      title="Selecteer alles"
                    />
                  </th>
                  <th><SortHeader field="external_id" sort={sort} setSort={setSort}>Order</SortHeader></th>
                  <th>Item</th>
                  <th><SortHeader field="sales_channel_name" sort={sort} setSort={setSort}>Kanaal</SortHeader></th>
                  <th><SortHeader field="workflow_state" sort={sort} setSort={setSort}>Status</SortHeader></th>
                  <th><SortHeader field="customer_country" sort={sort} setSort={setSort}>Land</SortHeader></th>
                  {showTracking && <th>Tracking</th>}
                  <th className="text-right"><SortHeader field="total_amount" sort={sort} setSort={setSort}>Omzet</SortHeader></th>
                  <th className="text-right"><SortHeader field="total_margin" sort={sort} setSort={setSort}>Marge</SortHeader></th>
                  <th><SortHeader field="order_date" sort={sort} setSort={setSort} defaultDir="desc">Datum</SortHeader></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedOrders.map((o) => {
                  const moreItems = o.item_count > 1 ? o.item_count - 1 : 0;
                  const dotColor = o.status === "CANCELED"
                    ? "var(--color-negative)"
                    : (o.workflow_state && WORKFLOW_DOTS[o.workflow_state]) || "var(--color-text-muted)";
                  return (
                    <tr
                      key={o.id}
                      onClick={(e) => {
                        // Click op checkbox-cell of zijn input: niet navigeren
                        const tgt = e.target as HTMLElement;
                        if (tgt.tagName === "INPUT" || tgt.closest('input[type="checkbox"]') || tgt.closest('a')) return;
                        navigate(`/orders/${o.id}`);
                      }}
                      style={{
                        cursor: "pointer",
                        opacity: o.status === "CANCELED" ? 0.55 : 1,
                        background: selectedIds.has(o.id) ? "var(--color-accent)11" : undefined,
                      }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(o.id)}
                          onChange={() => toggleOne(o.id)}
                        />
                      </td>
                      <td>
                        <strong>{o.external_id ?? `#${o.id}`}</strong>
                        <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "var(--font-mono)" }}>
                          id {o.id}
                        </div>
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        {o.first_item_sku ? (
                          <>
                            <div style={{ fontSize: "var(--fs-sm)" }}>
                              <span className="text-mono" style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
                                {fmtNum.format(o.first_item_qty ?? 1)}× {o.first_item_sku}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {o.first_item_title ?? "—"}
                              {moreItems > 0 && <span style={{ marginLeft: 4 }}>+ {moreItems} meer</span>}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: "var(--color-text-subtle)" }}>—</span>
                        )}
                      </td>
                      <td style={{ fontSize: "var(--fs-sm)" }}>{o.sales_channel_name ?? "—"}</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 4, background: dotColor }} />
                          <span style={{ fontSize: "var(--fs-sm)" }}>
                            {o.workflow_state ? (WORKFLOW_LABELS[o.workflow_state] || o.workflow_state) : o.status}
                          </span>
                        </span>
                      </td>
                      <td className="text-mono" style={{ fontSize: "var(--fs-sm)" }}>{o.customer_country ?? "—"}</td>
                      {showTracking && (
                        <td style={{ fontSize: 11 }} onClick={(e) => e.stopPropagation()}>
                          {o.tracking_number ? (
                            o.tracking_url ? (
                              <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="text-mono">
                                {o.tracking_number.slice(0, 18)}{o.tracking_number.length > 18 ? "…" : ""}
                              </a>
                            ) : (
                              <span className="text-mono">{o.tracking_number.slice(0, 18)}{o.tracking_number.length > 18 ? "…" : ""}</span>
                            )
                          ) : <span style={{ color: "var(--color-text-subtle)" }}>—</span>}
                          {o.carrier && <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{o.carrier}</div>}
                        </td>
                      )}
                      <td className="text-right text-mono">{canSeeAmounts ? fmtEUR.format(Number(o.total_amount)) : "—"}</td>
                      <td className="text-right text-mono" style={{
                        color: canSeeAmounts && Number(o.total_margin) < 0 ? "var(--color-negative)" : undefined,
                      }}>{canSeeAmounts ? fmtEUR.format(Number(o.total_margin)) : "—"}</td>
                      <td title={fmtAbsolute(o.order_date)} style={{ color: "var(--color-text-muted)", fontSize: "var(--fs-sm)", whiteSpace: "nowrap" }}>
                        {fmtRelative(o.order_date)}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {o.has_returns && <span title="Heeft retours" style={{ fontSize: 14 }}>↩️</span>}
                        {o.has_notes && <span title="Heeft notities" style={{ fontSize: 14, marginLeft: 4 }}>📝</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
